// Phase MHM-4 · Hub Analytics — 7 datasets + Leaflet heatmap.
import { useEffect, useState, useCallback } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import { Loader2, RefreshCw, TrendingUp, TrendingDown } from "lucide-react";
import { BarChart, Bar, LineChart, Line, AreaChart, Area, PieChart, Pie, Cell,
         XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";
import { toast } from "sonner";
import api from "../../services/api";
import MHMSubNav from "../../components/hub/MHMSubNav";
import SlaAlertBadge from "../../components/hub/SlaAlertBadge";
import RoleGate from "../../components/RoleGate";

const RANGES = ["7d", "30d", "90d"];
const PIE_COLORS = ["#ef4444", "#f59e0b", "#22c55e", "#3b82f6", "#a855f7", "#06b6d4", "#64748b", "#71717a"];

function Card({ title, subtitle, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm" data-testid={testid}>
      <h3 className="text-sm font-semibold text-slate-700 font-['Outfit']">{title}</h3>
      {subtitle && <p className="text-xs text-slate-500 mb-2">{subtitle}</p>}
      {children}
    </div>
  );
}

function MiniSpark({ data }) {
  return (
    <ResponsiveContainer width={60} height={24}>
      <LineChart data={data}>
        <Line dataKey="v" stroke="#7c3aed" strokeWidth={1.5} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default function HubAnalytics() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const paths = ["orders-by-area", "peak-timings", "top-zones",
                     "delay-heatmap", "failed-reasons",
                     "agent-productivity", "hub-efficiency"];
      const res = await Promise.all(paths.map((p) =>
        api.get(`/hub/analytics/${p}?range=${range}`).then(r => [p, r.data])
                .catch(() => [p, { rows: [], no_data: true }])));
      setData(Object.fromEntries(res));
    } catch (e) { toast.error(e.userMessage || "Load failed"); }
    finally { setLoading(false); }
  }, [range]);
  useEffect(() => { load(); }, [load]);

  const heatCenter = data["delay-heatmap"]?.rows?.[0]
    ? [data["delay-heatmap"].rows[0].lat, data["delay-heatmap"].rows[0].lng]
    : [12.9716, 77.5946];

  return (
    <RoleGate allow={["main_hub_manager", "admin", "founder", "ceo", "micro_hub_lead"]}>
      <MHMSubNav />
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="mhm-analytics-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Outfit']" data-testid="mhm-analytics-title">
              Analytics
            </h1>
            <p className="text-sm text-slate-500">Hub operational insights · {range}</p>
          </div>
          <div className="flex items-center gap-2">
            <SlaAlertBadge />
            <div className="flex gap-1" data-testid="mhm-analytics-ranges">
              {RANGES.map((r) => (
                <button key={r} onClick={() => setRange(r)}
                        data-testid={`mhm-analytics-range-${r}`}
                        className={`text-sm px-3 py-1.5 rounded-full ${
                          range === r ? "bg-violet-600 text-white" : "bg-slate-100 hover:bg-slate-200 text-slate-700"
                        }`}>{r}</button>
              ))}
            </div>
            <button onClick={load} data-testid="mhm-analytics-refresh"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 hover:bg-slate-50 rounded-lg">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="mhm-analytics-grid">
          {/* 1. Orders by area */}
          <Card title="Orders by area" subtitle="Top 10 destination pin-prefixes" testid="mhm-analytics-by-area">
            <div className="h-[220px]">
              <ResponsiveContainer>
                <BarChart data={(data["orders-by-area"]?.rows || []).slice(0, 10)} layout="vertical" margin={{ left: 24 }}>
                  <CartesianGrid stroke="#eef2f7" />
                  <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
                  <YAxis type="category" dataKey="area" tick={{ fontSize: 10 }} width={80} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#7c3aed" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* 2. Peak timings */}
          <Card title="Peak timings · 24h heatmap" subtitle="Orders per hour-of-day" testid="mhm-analytics-peak">
            <div className="h-[220px]">
              <ResponsiveContainer>
                <AreaChart data={data["peak-timings"]?.rows || []}>
                  <CartesianGrid stroke="#eef2f7" />
                  <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                  <Tooltip />
                  <Area dataKey="count" stroke="#7c3aed" fill="#c4b5fd" fillOpacity={0.4} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* 3. Top zones */}
          <Card title="Top delivery zones" subtitle="Volume + growth vs prior period" testid="mhm-analytics-zones">
            <div className="h-[220px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-slate-500">
                  <tr><th className="text-left p-1">Area</th><th className="text-right">Orders</th><th className="text-right">Revenue</th><th className="text-right">Growth</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data["top-zones"]?.rows || []).slice(0, 10).map((z) => (
                    <tr key={z.area}>
                      <td className="p-1 font-mono">{z.area}</td>
                      <td className="text-right">{z.count}</td>
                      <td className="text-right">₹{z.revenue}</td>
                      <td className={`text-right inline-flex items-center justify-end gap-1 ${z.growth_pct >= 0 ? "text-green-600" : "text-rose-600"}`}>
                        {z.growth_pct >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                        {z.growth_pct}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* 4. Delay heatmap */}
          <Card title="Delay heatmap" subtitle="Drop points with delays > SLA (clustered ~500m)" testid="mhm-analytics-heatmap">
            <div className="h-[220px] rounded-lg overflow-hidden border border-slate-100">
              <MapContainer center={heatCenter} zoom={10} style={{ height: "100%" }} scrollWheelZoom={false}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OSM" />
                {(data["delay-heatmap"]?.rows || []).map((p, i) => (
                  <CircleMarker key={i} center={[p.lat, p.lng]}
                                radius={Math.min(20, 4 + p.intensity * 1.5)}
                                pathOptions={{ color: "#dc2626", fillColor: "#fecaca", fillOpacity: 0.55 }}>
                    <Popup><div className="text-xs">Delayed: {p.intensity}</div></Popup>
                  </CircleMarker>
                ))}
              </MapContainer>
            </div>
          </Card>

          {/* 5. Failed reasons pie */}
          <Card title="Failed delivery reasons" subtitle="Distribution of failure categories" testid="mhm-analytics-reasons">
            <div className="h-[220px]">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={data["failed-reasons"]?.rows || []} dataKey="count" nameKey="reason"
                       outerRadius={70} cx="50%" cy="50%" label={({ reason, pct }) => `${reason}: ${pct}%`}>
                    {(data["failed-reasons"]?.rows || []).map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* 6. Agent productivity */}
          <Card title="Agent productivity" subtitle="Ranked by deliveries" testid="mhm-analytics-productivity">
            <div className="h-[220px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase text-slate-500">
                  <tr><th className="text-left p-1">#</th><th className="text-left">Agent</th><th className="text-right">Deliveries</th><th className="text-right">On-time</th><th>Trend</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data["agent-productivity"]?.rows || []).slice(0, 8).map((a) => (
                    <tr key={a.agent_user_id}>
                      <td className="p-1 text-xs">{a.rank}</td>
                      <td className="text-xs">{a.name}</td>
                      <td className="text-right text-xs">{a.deliveries}</td>
                      <td className="text-right text-xs">{a.on_time_pct}%</td>
                      <td><MiniSpark data={[{v: 0},{v: a.deliveries/4},{v: a.deliveries/2},{v: a.deliveries}]}/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* 7. Hub efficiency leaderboard */}
          <Card title="Micro hub efficiency leaderboard" subtitle="Composite score · 0.4·SLA + 0.3·util + 0.3·throughput" testid="mhm-analytics-efficiency">
            <div className="h-[220px]">
              <ResponsiveContainer>
                <BarChart data={(data["hub-efficiency"]?.rows || []).slice(0, 8)} layout="vertical" margin={{ left: 32 }}>
                  <CartesianGrid stroke="#eef2f7" />
                  <XAxis type="number" tick={{ fontSize: 10 }} domain={[0, 100]} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={140} />
                  <Tooltip />
                  <Bar dataKey="score" fill="#22c55e" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      </div>
    </RoleGate>
  );
}
